import { Link } from "react-router-dom";
import { ArrowRight, Hammer, Building2, Wrench, ClipboardCheck, Users2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/lib/seo";
import { Reveal, SectionHeading, CTASection, ComplianceNote, CheckItem, stagger, fadeUp } from "@/components/shared/Shared";
import { motion } from "framer-motion";

const IMG_HERO = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=1600&auto=format&fit=crop";

export default function Development() {
  const leistungen = [
    {
      icon: Building2,
      title: "Projektentwicklung",
      text: "Eigene Entwicklung und Bestandsoptimierung von Wohnimmobilien – mit Fokus auf nachhaltige Vermietbarkeit statt kurzfristiger Effekte.",
    },
    {
      icon: Hammer,
      title: "Sanierung",
      text: "Herrichtung von Wohnungen vor der Neuvermietung – nach festen Ausstattungsstandards: von Grundrissen über Bäder und Böden bis zur Elektrik. Fachbetrieb statt Preisliste.",
    },
    {
      icon: Wrench,
      title: "Hausmeisterservice",
      text: "Laufende Betreuung der Objekte im Bestand – damit Qualität und Termine im Haus bleiben und Eigentümer einen festen Ansprechpartner haben.",
    },
  ];

  return (
    <div className="bg-white">
      <Seo
        title="Eleven Development – Projektentwicklung, Sanierung & Objektbetreuung"
        description="Eigene Projektentwicklung, Sanierung nach festen Standards und Hausmeisterservice: Eleven Development hält Qualität und Termine im Haus – mit über 400 Wohnungen in eigener Verwaltung."
      />

      {/* HERO */}
      <section className="bg-[#111318] noise-overlay accent-hairline" data-testid="dev-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div variants={stagger} initial="hidden" animate="show" className="lg:col-span-7">
              <motion.div variants={fadeUp} className="flex items-center gap-3">
                <span className="h-px w-8 bg-[#FF3C20]" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60">Eleven Development</p>
              </motion.div>
              <motion.h1 variants={fadeUp} className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] text-white" data-testid="dev-hero-headline">
                Qualität und Termine bleiben im Haus.
              </motion.h1>
              <motion.p variants={fadeUp} className="mt-5 text-base md:text-lg text-white/60 leading-relaxed max-w-[60ch]">
                Eleven Development bündelt Projektentwicklung, Sanierung und Objektbetreuung innerhalb der Gruppe. So stellen wir sicher, dass jede Wohnung, die über Eleven Real Estate vermittelt wird, unseren Standards entspricht – vor dem Einzug des Mieters, nicht danach.
              </motion.p>
              <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-[6px] h-12 px-6 bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold" data-testid="dev-hero-primary-cta">
                  <Link to="/anfrage/erstgespraech">Gespräch anfragen <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-[6px] h-12 px-6 border-white/25 text-white bg-transparent hover:bg-white/5 hover:text-white hover:border-white" data-testid="dev-hero-secondary-cta">
                  <Link to="/real-estate">Zur Kapitalanlage</Link>
                </Button>
              </motion.div>
            </motion.div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative rounded-[8px] overflow-hidden ring-1 ring-white/10 ">
                <img src={IMG_HERO} alt="Projektentwicklung und Sanierung – Eleven Development" className="w-full h-[420px] object-cover" loading="eager" decoding="async" data-testid="dev-hero-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111318]/60 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEISTUNGEN */}
      <section className="bg-white py-14 sm:py-20" data-testid="dev-leistungen">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="Leistungen"
            title="Drei Aufgaben unter einem Dach"
            subtitle="Was andere an Subunternehmer vergeben, halten wir bewusst in der Gruppe."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {leistungen.map((l, i) => (
              <Reveal key={l.title} className="rounded-[8px] border border-border bg-[#F6F7F8] p-8 card-hover" data-testid={`dev-leistung-${i + 1}`}>
                <div className="h-11 w-11 rounded-[6px] bg-[#111318] flex items-center justify-center">
                  <l.icon className="h-5 w-5 text-[#FF3C20]" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-[#111318]">{l.title}</h3>
                <p className="mt-3 text-sm text-[#3B4452] leading-relaxed">{l.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ARBEITSWEISE + VERWALTUNG */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20" data-testid="dev-arbeitsweise">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <SectionHeading
                eyebrow="Arbeitsweise"
                title="Saniert wird vor der Neuvermietung – nicht auf Zuruf"
                subtitle="Jede Wohnung wird nach einheitlichen Ausstattungspaketen hergerichtet, bevor ein neuer Mieter einzieht. Das sichert Vermietbarkeit und Substanz – und schafft für Käufer wie Mieter klare Verhältnisse."
              />
              <div className="mt-7 space-y-3">
                <CheckItem>Feste Standards für Bäder, Böden, Elektrik und Grundrisse</CheckItem>
                <CheckItem>Fertigstellung vor Übergabe an den Mieter</CheckItem>
                <CheckItem>Enge Verzahnung mit Eleven Real Estate – ein Prozess, ein Standard</CheckItem>
              </div>
            </div>
            <div className="space-y-6">
              <Reveal className="rounded-[8px] bg-[#111318] noise-overlay p-8" data-testid="dev-kpi-card">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-[6px] bg-white/5 border border-white/10 flex items-center justify-center">
                    <Users2 className="h-5 w-5 text-[#FF3C20]" />
                  </div>
                  <div>
                    <p className="font-mono-num text-3xl font-semibold text-white">400+</p>
                    <p className="text-sm text-white/55">Wohnungen in eigener Verwaltung</p>
                  </div>
                </div>
                <p className="mt-4 text-sm text-white/55 leading-relaxed">
                  Die laufende Verwaltung und Betreuung der Objekte erfolgt innerhalb der Gruppe – vom Hausmeisterservice bis zur Mieterkommunikation.
                </p>
              </Reveal>
              <Reveal className="rounded-[8px] bg-white border border-border p-8" data-testid="dev-partner-card">
                <div className="flex items-center gap-3">
                  <ClipboardCheck className="h-5 w-5 text-[#E8241B]" />
                  <h3 className="font-display text-lg font-semibold text-[#111318]">Partner: RELAGO Beteiligungs GmbH</h3>
                </div>
                <p className="mt-3 text-sm text-[#3B4452] leading-relaxed">
                  Strategischer Bauträger- und Projektentwicklungspartner der Gruppe mit Fokus auf Wohnimmobilien, Bestandsoptimierung und nachhaltige Immobilienentwicklung.
                </p>
                <p className="mt-4 text-xs text-[#6B7280]">
                  Wernhaldenstraße 86 · 70184 Stuttgart ·{" "}
                  <a href="https://relago.de" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline underline-offset-2 text-[#111318] hover:text-[#E8241B] transition-colors" data-testid="dev-relago-link">
                    relago.de <ExternalLink className="h-3 w-3" />
                  </a>
                </p>
              </Reveal>
            </div>
          </div>
          <div className="mt-10 max-w-4xl">
            <ComplianceNote>
              Eleven Development erbringt Leistungen innerhalb der II Eleven Gruppe und für deren Bestandsobjekte. Angaben zu Referenzprojekten oder Leistungen für Dritte machen wir bewusst nicht pauschal – sprechen Sie uns für Details gerne an.
            </ComplianceNote>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Lernen Sie unseren Sanierungs- und Objektprozess kennen."
        subtitle="Ob als Käufer einer Bestandswohnung oder Eigentümer im Bestand – wir zeigen Ihnen gerne, wie Entwicklung, Sanierung und Betreuung bei Eleven ineinandergreifen."
        primaryLabel="Gespräch anfragen"
        primaryTo="/anfrage/erstgespraech"
        secondaryLabel="Zur Kapitalanlage mit Bestandswohnungen"
        secondaryTo="/real-estate"
      />
    </div>
  );
}
